// Layer 3 (Transformation): the Cost & revenue lead band (locked comp
// `.impeccable/mocks/decision/cost.html`, variant A, picked 25 Sep): the
// four-cell margin strip, the operating-cost categories ranked by their
// overrun against Budget, and the six tab headlines. Pure projections of the
// transforms the tabs already read; no figure is computed here that a tab does
// not already print, and nothing here formats money another way.

import { improvementKpis } from './ImprovementMatrix.transform';
import { improvementPipeline } from './ImprovementPipeline.transform';
import { costPL, formatCostPLCell, formatCostPLDelta, ZERO_DELTA } from './CostPLTable.transform';
import { costPerTonneView } from './CostPerTonne.transform';
import { nsrCards } from './NsrTab.transform';
import { costHeadlineCards, costPeriod, type CostCategoryName, type CostPeriod, type CostTab } from './Scene.transform';
import { CURRENCY_GLYPH } from '@/versions/v1/shared/money';

export type CostStripCell = {
  /** Caps stage name (`Net revenue`). */
  name: string;
  /** The primary figure without its per-unit suffix (`US$72.25`). */
  figure: string;
  /** A per-unit suffix set small on the figure's baseline (`/dst`), or null. */
  unit: string | null;
  /** The comparison's lead term (`−5%`). */
  lead: string;
  /** The rest of the comparison (`vs design potential US$1.55M`). */
  rest: string;
  /** The lead term takes the warn tone only when it is the abnormal reading:
   *  operating cost over its Budget. Revenue and margin read against design
   *  potential, which is not a budget miss, so they stay ink. */
  leadTone: 'ink' | 'warn';
};

/** The P&L's four headline figures as one strip read left to right: net
 *  revenue less operating cost is the margin, per dst milled. Each cell is
 *  the same `costHeadlineCards` figure and caption the P&L headline printed,
 *  its caption split at the first space into lead term and rest. */
export function costStrip(period: CostPeriod): readonly CostStripCell[] {
  return costHeadlineCards(costPeriod(period)).map((card) => {
    const split = card.sub.indexOf(' ');
    const slash = card.value.indexOf('/');
    return {
      name: card.label,
      figure: slash > 0 ? card.value.slice(0, slash) : card.value,
      unit: slash > 0 ? card.value.slice(slash) : null,
      lead: card.sub.slice(0, split),
      rest: card.sub.slice(split + 1),
      leadTone: card.label === 'Operating cost' && card.tone === 'warn' ? 'warn' : 'ink',
    };
  });
}

export type CostOverrunRow = {
  rank: number;
  category: CostCategoryName;
  budget: string;
  actual: string;
  delta: string;
  deltaRaw: number;
  /** The overrun as a share of the largest overrun, for the rank bar; 0 when
   *  the category did not run over. */
  ratio: number;
  /** `warn` over Budget, `ok` under, `zero` exactly on it, `neutral` for the
   *  volume-driven reagent variance (extra tonnage at the budget rate). */
  tone: 'warn' | 'ok' | 'zero' | 'neutral';
  volumeDriven: boolean;
};

/** The five categories' Total triplet from the P&L (`costPL`), ranked by the
 *  money each ran over its Budget. The P&L grid keeps only its Fixed and
 *  Variable split, so each category total is stated once, here. */
export function costOverruns(period: CostPeriod): readonly CostOverrunRow[] {
  const pl = costPL(period);
  const ranked = [...pl.rows].sort((a, b) => b.total.delta - a.total.delta);
  const max = Math.max(0, ...ranked.map((row) => row.total.delta));
  return ranked.map((row, index) => {
    const delta = row.total.delta;
    const volumeDriven = pl.reagentVarianceVolumeDriven && row.key === 'Reagents';
    return {
      rank: index + 1,
      category: row.key as CostCategoryName,
      budget: formatCostPLCell(row.total.budget),
      actual: formatCostPLCell(row.total.actual),
      delta: delta === 0 ? ZERO_DELTA : formatCostPLDelta(delta),
      deltaRaw: delta,
      ratio: max > 0 && delta > 0 ? delta / max : 0,
      tone: delta === 0 ? 'zero' : volumeDriven ? 'neutral' : delta > 0 ? 'warn' : 'ok',
      volumeDriven,
    };
  });
}

/** Each tab's headline figure, set beside its label (the Downtime tab idiom).
 *  Tabs 1–3 move with the period; tabs 4–6 read the whole register. */
export function costTabHeadlines(period: CostPeriod): Record<CostTab, string> {
  const pl = costPL(period);
  const kpis = improvementKpis();
  const lanes = improvementPipeline().lanes;
  const count = (stage: string) => lanes.find((lane) => lane.stage === stage)?.count ?? 0;
  return {
    pl: formatCostPLDelta(pl.total.total.delta),
    'cost-per-tonne': costPerTonneView(period).cards[0]!.value,
    nsr: nsrCards(period).zn.value,
    matrix: `${kpis.quickWins} quick wins`,
    improvements: `${kpis.priced} of ${kpis.total} priced`,
    pipeline: `${count('approved')} approved · ${count('capital request')} requested`,
  };
}

/** The daily-margin card's unit and comparator, named once in its header. */
export const DAILY_MARGIN_META = `${CURRENCY_GLYPH}K · vs design potential`;

/** The P&L tab's bridge card meta: the period's range and the bridge's unit.
 *  The bridge is the GM landing's (`ebitdaWaterfall`), cut to this period. */
export function costBridgeMeta(period: CostPeriod): string {
  return `${costPeriod(period).dateRange} · ${CURRENCY_GLYPH}K`;
}
